/**
 * @file
 * @author zhousheng
 */
import * as React from 'react';
import useMemo from 'rc-util/lib/hooks/useMemo';
import IconContext from 'acud-icon/lib/components/IconContext';
import LocaleProvider from '../locale-provider';
import LocaleReceiver from '../locale-provider/LocaleReceiver';
import { RenderEmptyHandler } from './renderEmpty';
import { ConfigConsumer, ConfigContext, CSPConfig, DirectionType, ConfigConsumerProps } from './context';
import SizeContext, { SizeContextProvider } from './SizeContext';
import { FormProvider } from '../form/context';
import { unstableSetRender } from './UnstableContext';
export { RenderEmptyHandler, ConfigContext, ConfigConsumer, CSPConfig, DirectionType, ConfigConsumerProps };
export const configConsumerProps = [
    'getTargetContainer',
    'getPopupContainer',
    'rootPrefixCls',
    'getPrefixCls',
    'renderEmpty',
    'csp',
    'autoInsertSpaceInButton',
    'locale',
    'pageHeader'
];
// These props is used by `useContext` directly in sub component
const PASSED_PROPS = [
    'getTargetContainer',
    'getPopupContainer',
    'renderEmpty',
    'pageHeader',
    'input'
];
export const defaultPrefixCls = 'acud';
export const defaultIconPrefixCls = `${defaultPrefixCls}icon`;
let globalPrefixCls = defaultPrefixCls;
let globalIconPrefixCls;
function getGlobalIconPrefixCls(prefixCls) {
    return globalIconPrefixCls || (prefixCls ? `${prefixCls}icon` : defaultIconPrefixCls);
}
const setGlobalConfig = ({ prefixCls, iconPrefixCls, unstableRender, }) => {
    if (prefixCls !== undefined) {
        globalPrefixCls = prefixCls;
    }
    if (iconPrefixCls !== undefined) {
        globalIconPrefixCls = iconPrefixCls;
    }
    if (unstableRender !== undefined) {
        unstableSetRender(unstableRender);
    }
};
export const globalConfig = () => ({
    getPrefixCls: (suffixCls, customizePrefixCls) => {
        if (customizePrefixCls) {
            return customizePrefixCls;
        }
        return suffixCls ? `${globalPrefixCls}-${suffixCls}` : globalPrefixCls;
    },
    getIconPrefixCls: getGlobalIconPrefixCls,
});
const ProviderChildren = props => {
    const { children, csp, autoInsertSpaceInButton, locale, componentSize, direction, space, virtual, dropdownMatchSelectWidth, legacyLocale, parentContext, iconPrefixCls: customIconPrefixCls, form } = props;
    const getPrefixCls = React.useCallback((suffixCls, customizePrefixCls) => {
        const { prefixCls } = props;
        if (customizePrefixCls) {
            return customizePrefixCls;
        }
        const mergedPrefixCls = prefixCls || parentContext.getPrefixCls('');
        return suffixCls ? `${mergedPrefixCls}-${suffixCls}` : mergedPrefixCls;
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentContext.getPrefixCls]);
    const iconPrefixCls = customIconPrefixCls
        || parentContext.iconPrefixCls
        || getGlobalIconPrefixCls(getPrefixCls(''));
    const config = Object.assign(Object.assign({}, parentContext), { csp,
        autoInsertSpaceInButton, locale: locale || legacyLocale, direction,
        space,
        virtual,
        dropdownMatchSelectWidth,
        form,
        getPrefixCls });
    // Pass the props used by `useContext` directly with child component.
    // These props should merged into `config`.
    PASSED_PROPS.forEach(propName => {
        const propValue = props[propName];
        if (propValue) {
            config[propName] = propValue;
        }
    });
    const memoedConfig = useMemo(() => config, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    config, (prevConfig, currentConfig) => {
        const prevKeys = Object.keys(prevConfig);
        const currentKeys = Object.keys(currentConfig);
        return (prevKeys.length !== currentKeys.length
            || prevKeys.some(key => prevConfig[key] !== currentConfig[key]));
    });
    const memoIconContextValue = React.useMemo(() => ({ prefixCls: iconPrefixCls }), [iconPrefixCls]);
    let childNode = children;
    // Additional Form provider
    if (locale) {
        childNode = (<LocaleProvider locale={locale}>
                {childNode}
            </LocaleProvider>);
    }
    if (iconPrefixCls) {
        childNode = (<IconContext.Provider value={memoIconContextValue}>{childNode}</IconContext.Provider>);
    }
    if (componentSize) {
        childNode = <SizeContextProvider size={componentSize}>{childNode}</SizeContextProvider>;
    }
    if (form) {
        childNode = <FormProvider {...form}>{childNode}</FormProvider>;
    }
    return <ConfigContext.Provider value={memoedConfig}>{childNode}</ConfigContext.Provider>;
};
const ConfigProvider = props => {
    React.useEffect(() => {
        if (props.direction) {
            // message.config({
            // rtl: props.direction === 'rtl',
            // });
            // notification.config({
            // rtl: props.direction === 'rtl',
            // });
        }
    }, [props.direction]);
    return (<LocaleReceiver>
            {(_, __, legacyLocale) => (<ConfigConsumer>
                    {context => (<ProviderChildren parentContext={context} legacyLocale={legacyLocale} {...props}/>)}
                </ConfigConsumer>)}
        </LocaleReceiver>);
};
/** @private internal Usage. do not use in your production */
ConfigProvider.ConfigContext = ConfigContext;
ConfigProvider.SizeContext = SizeContext;
ConfigProvider.config = setGlobalConfig;
export default ConfigProvider;
